import { type IThemePalette } from "../types";
import { MaterialElevations } from "../base";

export interface IPopupCardTokens {
  backgroundColor: string;
  borderColor: string;
  borderRadius: number;
  popupRadius: number;
  borderWidth: number;
  elevation: typeof MaterialElevations.level3;
  zIndex: number;
}

export interface IPopupHeaderTokens {
  backgroundColor: string;
  borderBottomColor: string;
  titleColor: string;
  subtitleColor: string;
  iconColor: string;
  closeButtonBg: string;
  closeButtonIconColor: string;
  minHeight: number;
  paddingHorizontal: number;
  paddingVertical: number;
}

export interface IPopupFooterTokens {
  backgroundColor: string;
  borderTopColor: string;
  paddingHorizontal: number;
  paddingVertical: number;
  gap: number;
}

export interface IPopupMenuItemTokens {
  restingBg: string;
  hoverBg: string;
  selectedBg: string;
  destructiveHoverBg: string;
  textColor: string;
  mutedTextColor: string;
  destructiveTextColor: string;
  accentBarWidth: number;
  accentBarColor: string;
  destructiveAccentBarColor: string;
  dividerColor: string;
}

export interface IKebabTriggerTokens {
  sizeStandard: number;
  sizeCompact: number;
  iconSizeStandard: number;
  iconSizeCompact: number;
  restingBg: string;
  activeBg: string;
  restingBorderColor: string;
  hoverBorderColor: string;
  activeBorderColor: string;
  restingIconColor: string;
  activeIconColor: string;
  borderRadius: number;
}

export interface IPopupMenuTheme {
  card: IPopupCardTokens;
  header: IPopupHeaderTokens;
  footer: IPopupFooterTokens;
  item: IPopupMenuItemTokens;
  kebabTrigger: IKebabTriggerTokens;
}

/**
 * Pure factory generating the popup menu and flyout component theme.
 * Unifies ThemedPopupScreen, ThemedDropDown, and ThemedKebabMenu.
 *
 * @param palette - The active semantic theme palette.
 * @param isDark - Whether dark mode is currently active.
 * @returns Fully resolved IPopupMenuTheme.
 */
export function getPopupMenuTheme(palette: IThemePalette, isDark: boolean): IPopupMenuTheme {
  return {
    card: {
      backgroundColor: palette.card,
      borderColor: palette.border,
      borderRadius: 6,
      popupRadius: 8,
      borderWidth: 1,
      elevation: MaterialElevations.level3,
      zIndex: 9999,
    },
    header: {
      backgroundColor: "transparent",
      borderBottomColor: palette.border,
      titleColor: palette.text,
      subtitleColor: palette.textMuted,
      iconColor: palette.text,
      closeButtonBg: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(28, 35, 51, 0.08)",
      closeButtonIconColor: palette.text,
      minHeight: 38,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    footer: {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(28, 35, 51, 0.04)",
      borderTopColor: palette.border,
      paddingHorizontal: 10,
      paddingVertical: 6,
      gap: 8,
    },
    item: {
      restingBg: "transparent",
      hoverBg: palette.buttonSecondary,
      selectedBg: palette.backgroundSelected,
      destructiveHoverBg: isDark ? "rgba(239, 68, 68, 0.18)" : "rgba(171, 23, 23, 0.12)",
      textColor: palette.text,
      mutedTextColor: palette.textMuted,
      destructiveTextColor: palette.danger,
      accentBarWidth: 3,
      accentBarColor: palette.tint,
      destructiveAccentBarColor: palette.danger,
      dividerColor: palette.border,
    },
    kebabTrigger: {
      sizeStandard: 30,
      sizeCompact: 26,
      iconSizeStandard: 18,
      iconSizeCompact: 15,
      restingBg: "transparent",
      activeBg: palette.buttonSecondary,
      restingBorderColor: "rgba(161, 174, 209, 0.25)",
      hoverBorderColor: palette.buttonHoverBorder,
      activeBorderColor: palette.tint,
      restingIconColor: palette.textMuted,
      activeIconColor: palette.text,
      borderRadius: 4,
    },
  };
}
